import { defineStore } from 'pinia'
import { ref } from 'vue'
import { getBootstrapUser } from '@/api/endpoints/bootstrap'
import type { BootstrapProjectSummary, BootstrapUserResponse, UserRoleAssignment } from '@/types/bootstrap'

export type UserContextStatus = 'idle' | 'loading' | 'ready' | 'unauthenticated' | 'error'

interface AxiosLikeError {
  response?: { status?: number }
}

function statusOf (error: unknown): number | null {
  const candidate = error as AxiosLikeError | null
  return candidate?.response?.status ?? null
}

/**
 * Holder bruker-bootstrap-data (GA-flagg, prosjekter, globale roller). Hentes
 * én gang per sesjon via `ensureLoaded()`; idempotent og deduplikerer parallelle
 * kall. Router-guarden i #1223 bruker dette som Fase B (user-context).
 *
 * Speil til legacy stores skjer i task 1223/1225 når guarden flyttes inn.
 */
export const useUserContextStore = defineStore('userContext', () => {
  const status = ref<UserContextStatus>('idle')
  const isGlobalAdmin = ref(false)
  const setupSessionId = ref<string | null>(null)
  const projects = ref<BootstrapProjectSummary[]>([])
  const globalRoles = ref<UserRoleAssignment[]>([])
  const errorMessage = ref<string | null>(null)

  let inflight: Promise<void> | null = null

  async function ensureLoaded (): Promise<void> {
    if (status.value === 'ready') {
      return
    }
    if (inflight !== null) {
      return inflight
    }

    status.value = 'loading'
    errorMessage.value = null

    inflight = (async () => {
      try {
        const data: BootstrapUserResponse = await getBootstrapUser()
        isGlobalAdmin.value = data.user.isGlobalAdmin
        setupSessionId.value = data.user.setupSessionId
        projects.value = data.projects
        globalRoles.value = data.globalRoles
        status.value = 'ready'
      } catch (err) {
        const httpStatus = statusOf(err)
        if (httpStatus === 401) {
          status.value = 'unauthenticated'
        } else {
          status.value = 'error'
          errorMessage.value = err instanceof Error ? err.message : 'Kunne ikke laste brukerkontekst'
        }
      } finally {
        inflight = null
      }
    })()

    return inflight
  }

  function reset (): void {
    status.value = 'idle'
    isGlobalAdmin.value = false
    setupSessionId.value = null
    projects.value = []
    globalRoles.value = []
    errorMessage.value = null
    inflight = null
  }

  return {
    status,
    isGlobalAdmin,
    setupSessionId,
    projects,
    globalRoles,
    errorMessage,
    ensureLoaded,
    reset
  }
})
